import React, {
  useEffect, useState, useRef, Fragment
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { FormTemplate } from 'data/template/template.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import SipSettingForm from 'routes/SipSetting/components/SipSettingForm';
import { Ref } from 'routes/SipSetting/components/SipSettingForm/SipSettingForm';
import { RootState } from 'data/root.reducers';
import * as sipSettingSelectors from 'data/sipSetting/sipSetting.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as sipSettingActions from 'data/sipSetting/sipSetting.actions';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as dfniActions from 'data/deliveryFunctionNetworkInterface/deliveryFunctionNetworkInterface.actions';
import * as dfniSelectors from 'data/deliveryFunctionNetworkInterface/deliveryFunctionNetworkInterface.selectors';
import * as networkIdSelectors from 'data/networkId/networkId.selectors';
import * as networkIdActions from 'data/networkId/networkId.actions';
import { SipSetting } from 'data/sipSetting/sipSetting.types';
import { NetworkId } from 'data/networkId/networkId.types';
import ModalDialog from 'shared/modal/dialog';
import {
  getAuditDetailsToSubmit, getNetworkAuditDetailsToSubmit
} from '../../utils/sipSettingDataToSubmit';
import { sipSettingTemplate } from '../../templates';
import {
  adaptTemplateVisibilityMode,
  adaptTemplateOptions,
} from '../../utils/sipSettingTemplate.adapter';
import ErrorsDialog from '../../components/ErrorsDialog';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  deliveryFunctionId: state.global.selectedDF,
  selectedSipSetting: sipSettingSelectors
    .getSelectedSipSetting(state)[0],
  originalSelectedSipSetting: sipSettingSelectors
    .getSelectedSipSetting(state)[0],
  sipSetting: sipSettingSelectors.getSipSetting(state),
  sipSettingAdaptedTemplate: adaptTemplateVisibilityMode(sipSettingTemplate, 'view'),
  deliveryFunctionNetworkInterfaces: dfniSelectors.getDeliveryFunctionNetworkInterfaceList(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  isSipSettingLoading: sipSettingSelectors.isSipSettingLoading(state),
  sipSettingErrors: sipSettingSelectors.getSipSettingErrors(state),
  networkIdList: networkIdSelectors.getNetworkIdListToSipSettings(state),
});

const mapDispatch = {
  goToSipSettingEdit: sipSettingActions.goToSipSettingEdit,
  goToSipSettingCreate: sipSettingActions.goToSipSettingCreate,
  deleteSipSetting: sipSettingActions.deleteSipSetting,
  getSipSetting: sipSettingActions.getSipSetting,
  getDfnis: dfniActions.getDeliveryFunctionNetworkInterfaceList,
  getNetworkIdList: networkIdActions.getNetworkIdList,
  createNetworkId: networkIdActions.createNetworkId,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const SipSettingView = ({
  history,
  deliveryFunctionId,
  sipSetting,
  sipSettingAdaptedTemplate,
  deliveryFunctionNetworkInterfaces,
  userId,
  userName,
  isAuditEnabled,
  isSipSettingLoading,
  sipSettingErrors,
  networkIdList,
  getSipSetting,
  goToSipSettingEdit,
  getDfnis,
  goToSipSettingCreate,
  deleteSipSetting,
  getNetworkIdList,
  createNetworkId,
}: Props): JSX.Element => {
  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<SipSetting>>({});
  const [template, setTemplate] = useState(sipSettingAdaptedTemplate);
  const [networkId, setNetworkId] = useState<NetworkId | undefined>(undefined);
  const [networkIdCreated, setNetworkIdCreated] = useState(false);

  const [openConfirmActionDialog, setOpenConfirmActionDialog] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    getSipSetting(deliveryFunctionId);
    getDfnis(deliveryFunctionId);
    getNetworkIdList(deliveryFunctionId);
  }, [deliveryFunctionId, getSipSetting, getDfnis, getNetworkIdList]);

  useEffect(() => {

    if (sipSettingAdaptedTemplate == null) {
      return;
    }

    if (sipSetting != null) {
      setFormData(sipSetting);
    }

    let clonedTemplate = cloneDeep(sipSettingAdaptedTemplate) as FormTemplate;
    clonedTemplate = adaptTemplateOptions(clonedTemplate, deliveryFunctionNetworkInterfaces) as FormTemplate;
    clonedTemplate.formId = Date.now().toString();
    setTemplate(clonedTemplate);

  }, [sipSetting, sipSettingAdaptedTemplate, deliveryFunctionNetworkInterfaces, networkIdList]);

  useEffect(() => {
    if (networkIdList == null) {
      return;
    }
    const ss8PvoipGroupNetworkId = 'SS8_PVOIP_GROUP';
    const ss8PvoiNetworkId = networkIdList.find((item) => item.networkId === ss8PvoipGroupNetworkId);

    if (ss8PvoiNetworkId != null) {
      setNetworkId(ss8PvoiNetworkId);
    } else if (!networkIdCreated) {
        const newSs8PvoiNetworkId = {
          networkId: ss8PvoipGroupNetworkId,
          ownIPAddress: '0.0.0.0',
          portNumberRange: '1',
          startPortNumber: '1',
        };

        const payload = {
          data: newSs8PvoiNetworkId,
          auditDetails: getNetworkAuditDetailsToSubmit(newSs8PvoiNetworkId, null, isAuditEnabled, userName, userId, 'CREATE'),
        };

        createNetworkId(deliveryFunctionId, payload, history, false);
        setNetworkIdCreated(true);
      }
  }, [networkIdList]);

  const handleClickEdit = () => {
    if (sipSetting?.id != null || sipSetting?.sipInfo?.id != null) {
      goToSipSettingEdit(history);
    } else {
      goToSipSettingCreate(history);
    }
  };

  const handleClickDelete = () => {
    setOpenConfirmActionDialog(true);
  };

  const handleClickCancelDelete = () => {
    setOpenConfirmActionDialog(false);
  };

  const handleDeleteSipSetting = () => {

    if (sipSetting == null) {
      return;
    }

    const sipSettingData = cloneDeep(sipSetting);

    const payload = {
      data: sipSettingData,
      auditDetails: getAuditDetailsToSubmit(
        sipSettingData,
        sipSetting,
        isAuditEnabled,
        userId,
        userName,
        'DELETE',
      ),
    };

    deleteSipSetting(deliveryFunctionId, (sipSetting?.id || sipSetting?.sipInfo.id) as string, payload);
  };

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (formData != null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as SipSetting);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <SipSettingForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing={false}
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };

  const renderFormPanel = () => {
    return (
      <FormPanel title={t('sipSetting.view.title')}>
        <FormButtons
          isEditing={false}
          onClickEdit={handleClickEdit}
          onClickDelete={handleClickDelete}
        />
      </FormPanel>
    );
  };

  if (isSipSettingLoading || networkId == null) {
    return <div />;
  }

  return (
    <Fragment>
      <div data-testid="SipSettingView">
        <FormLayout>
          <FormLayoutContent>
            {renderFormPanel()}
            {renderForm()}
          </FormLayoutContent>
        </FormLayout>
      </div>
      <ModalDialog
        width="500px"
        isOpen={openConfirmActionDialog}
        title={t('sipSetting.delete.title')}
        customComponent={() => (
          <Fragment>
            {t('sipSetting.delete.message')}
          </Fragment>
        )}
        actionText={t('sipSetting.delete.confirm')}
        onSubmit={handleDeleteSipSetting}
        onClose={handleClickCancelDelete}
      />
      <ErrorsDialog data={sipSettingErrors} />
    </Fragment>
  );
};

export default withRouter(connector(SipSettingView));
